import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Redirect, withRouter, useLocation } from 'react-router-dom';
import { login } from 'auth/login/_redux';

const CheckAuthHOC = WrappedComponent => {
  const CheckAuthWrapped = ({
    profile,
    authorized,
    profileIsLoaded,
    forceVerification,
    lang,
    isEmailConfirmationEnabled,
    dispatch,
    partnershipConfig,
    ...props
  }) => {
    const { partnerCodeParamName } = partnershipConfig;
    const token = localStorage.getItem('jwtToken');
    const isVerified = profile && profile.status !== 'incomplete';
    const location = useLocation();
    const query = useMemo(() => queryString.parse(location.search), [location.search]);

    useEffect(() => {
      if ((query.broker_token || query.user_token || query.oneoff_token) && location.pathname === '/') {
        login(query, dispatch);
      }
    }, [dispatch, location.pathname, query]);

    const requestedPageIsAuth = location.pathname.includes('/auth');
    const requestedPageIsWidgets = location.pathname.includes('/widgets');
    const requestedPageIsWelcomeSetup = location.pathname.includes('/welcome-setup');
    const allowedRoutesIfForceVerificationIsEnabled = [
      '/documents',
      '/personal_profile',
      '/support',
      '/mail',
      '/change_password',
      '/personal_page',
      '/reverse-solicitation',
      '/create-account/demo',
      '/account',
    ];
    const requestedPageIsAllowedBeforeVerification = allowedRoutesIfForceVerificationIsEnabled.some(route =>
      location.pathname.includes(route)
    );

    // Agreement acknowledgment routes - routes allowed before agreement is acknowledged
    const allowedRoutesIfAgreementNotAcknowledged = [
      '/support',
      '/mail',
      '/change_password',
      '/personal_page',
      '/reverse-solicitation',
      '/create-account/demo',
      '/account',
      '/auth',
      '/widgets',
    ];
    const requestedPageIsAllowedBeforeAgreementAcknowledgment = allowedRoutesIfAgreementNotAcknowledged.some(route =>
      location.pathname.includes(route)
    );

    const isTfaSetupRequired = Boolean(
      profile && !profile?.tfaSettings?.enabled && profile?.tfaSettings?.setupRequired
    );

    const partnerCode = query[partnerCodeParamName];
    const salesCode = query.sales_code;
    const share = query.share;
    const isFirstRender = React.useRef(true);

    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!partnerCode) {
        sessionStorage.removeItem('partnerCode');
        localStorage.removeItem('partnerCode');
      }
      if (!salesCode) {
        sessionStorage.removeItem('salesCode');
        localStorage.removeItem('salesCode');
      }
      if (!share) {
        sessionStorage.removeItem('share');
        localStorage.removeItem('share');
      }
    }

    if (partnerCode) {
      sessionStorage.setItem('partnerCode', partnerCode);
      localStorage.setItem('partnerCode', partnerCode);
    }

    if (salesCode) {
      sessionStorage.setItem('salesCode', salesCode);
      localStorage.setItem('salesCode', salesCode);
    }

    if (share) {
      sessionStorage.setItem('share', share);
      localStorage.setItem('share', share);
    }

    if (token && authorized) {
      if (isTfaSetupRequired && !location.pathname.includes('change_password')) {
        return <Redirect to="/change_password" />;
      }

      if (requestedPageIsAuth) {
        return <Redirect to="/" />;
      }

      if (
        profileIsLoaded &&
        !isTfaSetupRequired &&
        !isVerified &&
        forceVerification &&
        !requestedPageIsAllowedBeforeVerification
      ) {
        if (window.location.hostname === 'my.spacemarkets.io') {
          return <Redirect to="/personal_profile" />;
        }

        return <Redirect to="/personal_page" />;
      }

      // Check agreement acknowledgment
      if (
        profileIsLoaded &&
        !isTfaSetupRequired &&
        !profile.isAgreementAcknowledged &&
        !requestedPageIsAllowedBeforeAgreementAcknowledgment
      ) {
        return <Redirect to="/personal_page" />;
      }

      return <WrappedComponent {...props} key={lang} />;
    }

    if (requestedPageIsAuth || requestedPageIsWidgets || requestedPageIsWelcomeSetup) {
      return <WrappedComponent {...props} key={lang} />;
    }

    // Invitation (sales_code) and partner (partner_code) links land on the root path,
    // send such visitors straight to the registration page
    const isInvitationOrPartnerLink =
      location.pathname === '/' && Boolean(query.sales_code || query[partnerCodeParamName]);

    return (
      <Redirect
        to={{ pathname: isInvitationOrPartnerLink ? '/auth/register' : '/auth/login', search: location.search }}
      />
    );
  };

  CheckAuthWrapped.propTypes = {
    authorized: PropTypes.bool.isRequired,
    lang: PropTypes.string,
    profile: PropTypes.object,
    profileIsLoaded: PropTypes.bool.isRequired,
    forceVerification: PropTypes.bool.isRequired,
    isEmailConfirmationEnabled: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  CheckAuthWrapped.defaultProps = {
    profile: {},
    lang: 'no-lang',
  };

  return CheckAuthWrapped;
};

CheckAuthHOC.propTypes = {
  WrappedComponent: PropTypes.element,
};

export default compose(
  withRouter,
  connect(
    state => ({
      authorized: state.auth.login.authorized,
      lang: state.locales && state.locales.lang,
      forceVerification: state.interfaceConfig?.kycSettings?.force || false,
      profile: state.user.profile,
      profileIsLoaded: state.user.profileIsLoaded,
      isEmailConfirmationEnabled: state.interfaceConfig?.emailConfirmation?.enabled || false,
      partnershipConfig: state.interfaceConfig?.partnership || {},
    }),
    dispatch => ({
      dispatch,
    })
  ),
  CheckAuthHOC
);
